import { useSelector } from 'react-redux';
import type { FunctionComponent } from 'react';

import { IssueFilterKeys } from '../../../../../shared/issuesV2';
import * as IssuesV2Store from '../../../../redux/issuesV2';
import { FilterPanel } from '../../../generic/Filter';
import FilterCardCheckboxes from '../../../generic/Filter/FilterCardCheckboxes';
import FilterCardNestedCheckboxes from '../../../generic/Filter/FilterCardNestedCheckboxes';
import useFetchIssuesAction from '../../IssuesInbox/hooks/useFetchIssuesAction';
import type { SelectOption, SelectOptionWithTooltip } from '../../../../types';
import type { SelectedOptionsProps } from '../../../generic/Filter';

const HAS_FIX_FILTER_LABELS: Record<string, Record<string, string>> = {
  patch: { label: 'Patch' },
  minor: { label: 'Minor' },
  major: { label: 'Major' },
  unknown: { label: 'Unknown', tooltip: 'A fix is available, however the upgrade distance could not be calculated.' },
};

// Build filter options for checkbox filter
const HAS_FIX_FILTER_OPTIONS: SelectOptionWithTooltip[] = Object.entries(HAS_FIX_FILTER_LABELS).map(
  ([value, label]) => ({
    label: label.label,
    value,
    tooltip: label.tooltip,
  }),
);

const NO_FIX_FILTER_LABEL: Record<string, string> = {
  none: 'No Fix',
};

// We provide the not nested options as their own array.
const NO_FIX_FILTER_OPTION: SelectOption[] = Object.entries(NO_FIX_FILTER_LABEL).map(([value, label]) => ({
  label,
  value,
}));

/**
 * Add a series of checkboxes for filtering Vuln Issues by "Upgrade Distance". A vuln issue can be either
 * unfixable, fixable by a specific upgrade distance, or have an unknown upgrade distance.
 */
const UpgradeDistanceFilter: FunctionComponent<SelectedOptionsProps> = ({ selectedOptions }) => {
  const filters = useSelector(IssuesV2Store.selectors.getFilter);
  const setFilter = useFetchIssuesAction(IssuesV2Store.actions.setFilter);

  const selectedCheckboxes = (selectedOptions ?? []).map((option: string) => ({
    label: HAS_FIX_FILTER_LABELS[option]?.label ?? '',
    value: option,
  }));

  const onNestedCheckboxChange = (selected: SelectOption[]) => {
    setFilter({
      ...filters,
      [IssueFilterKeys.UpgradeDistance]: selected.map(({ value }) => value),
    });
  };

  return (
    <FilterPanel>
      <FilterCardNestedCheckboxes
        label="Upgrade Distance"
        parentCheckboxLabel="Has Fix"
        options={HAS_FIX_FILTER_OPTIONS}
        values={selectedCheckboxes}
        onChange={onNestedCheckboxChange}
        testId="issues-vuln-fix-available"
      />
      <FilterCardCheckboxes
        options={NO_FIX_FILTER_OPTION}
        values={selectedCheckboxes}
        onChange={onNestedCheckboxChange}
      />
    </FilterPanel>
  );
};

export default UpgradeDistanceFilter;
